import CheckRounded from '@mui/icons-material/CheckRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { MediaCard } from './MediaCard';

export interface FeatureItem {
  label: string;
  value: string;
}

type Tone = 'success' | 'error';

interface FeatureColumnsProps {
  title: string;
  tone: Tone;
  columns: Record<string, FeatureItem[]>;
}

function FeatureTile({ label, value, tone }: FeatureItem & { tone: Tone }): ReactNode {
  const accent = tone === 'success' ? 'success.main' : 'error.main';
  const valueColor = tone === 'success' ? 'success.dark' : 'error.dark';
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        py: 2,
        px: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: (theme) =>
          alpha(tone === 'success' ? theme.palette.success.main : theme.palette.error.main, 0.2),
        background: (theme) =>
          `linear-gradient(135deg, ${tone === 'success' ? theme.palette.score.high.bg : theme.palette.score.low.bg}, ${theme.palette.background.paper} 85%)`,
      }}
    >
      <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 3, bgcolor: accent }} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: 8,
          fontWeight: 700,
          textTransform: 'uppercase',
          color: 'text.secondary',
          mb: 0.5,
        }}
      >
        {tone === 'success' ? (
          <CheckRounded sx={{ fontSize: 10, color: accent }} />
        ) : (
          <CloseRounded sx={{ fontSize: 10, color: accent }} />
        )}
        {label}
      </Box>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: valueColor }}>{value}</Typography>
    </Box>
  );
}

export function FeatureColumns({ title, tone, columns }: FeatureColumnsProps): ReactNode {
  const headerColor = tone === 'success' ? 'success.dark' : 'error.dark';
  const headerBg = tone === 'success' ? 'score.high.bg' : 'score.low.bg';
  return (
    <MediaCard sx={{ p: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: 1,
            bgcolor: headerBg,
            color: headerColor,
          }}
        >
          {tone === 'success' ? (
            <CheckRounded sx={{ fontSize: 13 }} />
          ) : (
            <WarningAmberRounded sx={{ fontSize: 13 }} />
          )}
        </Box>
        <Typography
          sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap' }}
        >
          {title}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 3,
          '@media print': { display: 'block' },
        }}
      >
        {Object.entries(columns).map(([category, items]) => (
          <Box key={category}>
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                textTransform: 'uppercase',
                color: 'text.secondary',
                mb: 2,
              }}
            >
              {category}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                '@media print': {
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                },
              }}
            >
              {items.map((item) => (
                <FeatureTile key={item.label} label={item.label} value={item.value} tone={tone} />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </MediaCard>
  );
}
